import React, { useRef, useState } from "react";
import { Container, Row, Col, Button, Form, Table } from "react-bootstrap";

// --- INLINE ERP UTILITIES ---
const generateDocumentRef = ({ prefix = "FIN", category = "INV", noticeId = "" }) => {
  const catCode = category ? category.substring(0, 3).toUpperCase() : "PAY";
  const idPart = noticeId || "78912";
  return `${prefix}-${catCode}-${idPart}`;
};

const getVendorSalutation = (vendorType) => {
  return {
    salute: "M/s.",
    title: "represented by",
  };
};

// --- DEMO DATA DEFAULT ---
const defaultPaymentNotice = {
  vendorName: "Apex Logistics & Supply Chain Ltd",
  contactPerson: "Marcus Vance",
  officeAddress: "Industrial Zone 4, Sector 12, Bangalore, KA",
  serviceScope: "Freight Logistics & Warehousing Services",
  fiscalPeriod: "FY 2026-27",
  noticeId: "INV-9081",
  billingPhase: "Phase 1 - Initial Milestone",
  baseAmount: "250000",
  taxAndLogistics: "45000",
  vendorType: "corporate",
};

export default function PaymentDemandLetter({ paymentNotice = defaultPaymentNotice }) {
  const printRef = useRef();

  const { salute, title } = getVendorSalutation(paymentNotice?.vendorType);

  const [formData, setFormData] = useState({
    refNo: generateDocumentRef({
      prefix: "FIN",
      category: "DEMAND",
      noticeId: paymentNotice?.noticeId,
    }),
    date: new Date().toLocaleDateString("en-IN"),

    vendorName: paymentNotice?.vendorName || "",
    contactPerson: paymentNotice?.contactPerson || "",
    address: paymentNotice?.officeAddress || "",
    serviceScope: paymentNotice?.serviceScope || "",
    fiscalPeriod: paymentNotice?.fiscalPeriod || "FY 2026-27",
    noticeId: paymentNotice?.noticeId || "",

    billingPhase: paymentNotice?.billingPhase || "Milestone 1",
    baseAmount: paymentNotice?.baseAmount || "250000",
    taxAndLogistics: paymentNotice?.taxAndLogistics || "45000",

    complianceFramework: "Corporate Finance & Tax Regulatory Policy",
    auditStandard: "GST & Statutory Audit Compliance Guidelines",

    companyName: "GLOBAL LOGISTICS & MANUFACTURING CORP",
    addressLine: "Corporate Finance Dept, Bangalore, India",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const total =
    Number(formData.baseAmount || 0) + Number(formData.taxAndLogistics || 0);

  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const win = window.open("", "", "width=900,height=700");

    win.document.write(`
      <html>
        <head>
          <title>Payment Demand Note</title>
          <style>
            body { 
              font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif; 
              padding: 160px 20px 20px; 
              color: #222; 
            }
            .print-container { line-height: 1.8; }
            .text-center { text-align: center; }
            .text-end { text-align: right; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            td, th { border: 1px solid #333; padding: 10px; text-align: center; }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `);

    win.document.close();
    win.print();
  };

  return (
    <Container className="mt-4">
      {/* INPUT FORM */}
      <Form className="mb-4">
        <Row className="mb-2">
          <Col>
            <Form.Control
              name="refNo"
              value={formData.refNo}
              onChange={handleChange}
              placeholder="Reference No."
            />
          </Col>
          <Col>
            <Form.Control
              name="date"
              value={formData.date}
              onChange={handleChange}
              placeholder="Issue Date"
            />
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Control
              name="vendorName"
              value={formData.vendorName}
              onChange={handleChange}
              placeholder="Vendor / Payee Name"
            />
          </Col>
          <Col>
            <Form.Control
              name="contactPerson"
              value={formData.contactPerson}
              onChange={handleChange}
              placeholder="Contact Representative"
            />
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Control
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Billing Address"
            />
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Control
              name="serviceScope"
              value={formData.serviceScope}
              onChange={handleChange}
              placeholder="Service Scope / Deliverables"
            />
          </Col>
          <Col>
            <Form.Control
              name="fiscalPeriod"
              value={formData.fiscalPeriod}
              onChange={handleChange}
              placeholder="Fiscal Period"
            />
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Control
              name="billingPhase"
              value={formData.billingPhase}
              onChange={handleChange}
              placeholder="Billing Milestone / Phase"
            />
          </Col>
          <Col>
            <Form.Control
              name="noticeId"
              value={formData.noticeId}
              onChange={handleChange}
              placeholder="Invoice / Tracking ID"
            />
          </Col>
        </Row>

        {/* FEES & FINANCIALS */}
        <Row className="mb-2">
          <Col>
            <Form.Control
              name="baseAmount"
              value={formData.baseAmount}
              onChange={handleChange}
              placeholder="Base Service Fees"
            />
          </Col>
          <Col>
            <Form.Control
              name="taxAndLogistics"
              value={formData.taxAndLogistics}
              onChange={handleChange}
              placeholder="Taxes & Surcharges"
            />
          </Col>
        </Row>
      </Form>

      {/* PRINT BUTTON */}
      <div className="text-end mt-3">
        <Button onClick={handlePrint} variant="primary">
          Print Demand Note
        </Button>
      </div>

      {/* DOCUMENT PREVIEW */}
      <div
        ref={printRef}
        style={{ padding: 20, border: "1px solid #000" }}
        className="print-container"
      >
        <div style={{ marginTop: "100px" }}>
          <Row>
            <Col>
              <strong>Ref No: {formData.refNo}</strong>
            </Col>
            <Col className="text-end">
              <strong>Date: {formData.date}</strong>
            </Col>
          </Row>

          {/* Title */}
          <Row className="mt-4">
            <Col className="text-center">
              <h2>
                <u>PAYMENT DEMAND NOTE</u>
              </h2>
            </Col>
          </Row>

          {/* Body */}
          <Row className="mt-4">
            <Col>
              <p>
                This letter serves as formal notification that vendor{" "}
                <strong>{salute} {formData.vendorName}</strong>, {title}{" "}
                <strong>{formData.contactPerson}</strong>, located at{" "}
                <strong>{formData.address}</strong>, is registered for contracted services in{" "}
                <strong>{formData.serviceScope}</strong> for the fiscal period{" "}
                <strong>{formData.fiscalPeriod}</strong>.
              </p>

              <p>
                The registered Invoice / Reference ID for this milestone is{" "}
                <strong>{formData.noticeId}</strong>.
              </p>

              <p>
                Payment obligations for <strong>{formData.billingPhase}</strong> are detailed as follows:
              </p>

              {/* TABLE */}
              <Table bordered>
                <thead>
                  <tr>
                    <th>Milestone / Phase</th>
                    <th>Base Amount (INR)</th>
                    <th>Taxes & Statutory Surcharges (INR)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{formData.billingPhase}</td>
                    <td>₹ {Number(formData.baseAmount).toLocaleString("en-IN")}</td>
                    <td>₹ {Number(formData.taxAndLogistics).toLocaleString("en-IN")}</td>
                  </tr>
                  <tr>
                    <td colSpan="3">
                      <strong>Total Amount Receivable: ₹ {total.toLocaleString("en-IN")}</strong>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col>
              <p>
                This financial schedule is structured under{" "}
                <strong>{formData.complianceFramework}</strong> and complies with{" "}
                <strong>{formData.auditStandard}</strong>.
              </p>
            </Col>
          </Row>

          {/* Footer */}
          <Row className="mt-5" style={{ paddingTop: "40px" }}>
            <Col className="text-end">
              <strong>CHIEF FINANCIAL OFFICER</strong>
              <div>Finance & Accounts Department</div>
            </Col>
          </Row>
        </div>
      </div>
    </Container>
  );
}